import React, { MouseEventHandler, useCallback, useContext, useMemo } from 'react'
import moment from 'moment'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import { isLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import LuxPlusLabelWithModal from 'luxPlus/components/LuxPlusLabelWithModal'
import GeoContext from 'contexts/geoContext'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { OfferPageActions, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import OfferPackagePartnershipLabel from 'components/Common/Labels/OfferPackagePartnershipLabel'
import TextLink from 'components/Luxkit/TextLink'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import InclusionsSection from 'components/OfferPage/FlashHotel/InclusionsSection'
import LineExclamationCircleIcon from 'components/Luxkit/Icons/line/LineExclamationCircleIcon'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import AccommodationPackageTile from '../../Accommodation/AccommodationPackageTile'
import AccommodationPackageTileContentArea from 'components/OfferPage/Accommodation/AccommodationPackageTileContentArea'
import AccommodationPackageTilePricingArea from 'components/OfferPage/Accommodation/AccommodationPackageTilePricingArea'
import AccommodationPackageTileDetails from '../../Accommodation/AccommodationPackageTileDetails'
import useIsAvailableForPurchase from '../../hooks/useIsAvailableForPurchase'
import FlashHotelOptionPriceDetailsVariant from './FlashHotelOptionPriceDetailsVariant'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LuxLoyaltyPoints from 'luxLoyalty/components/LuxLoyaltyPoints'
import Group from 'components/utils/Group'
import { generateLuxLoyaltyPointsCalculatorHotelOptions, generateLuxLoyaltyPointsCalculatorBundledFlightOptions } from 'luxLoyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import useLuxLoyaltyUserBenefit from 'luxLoyalty/hooks/useLuxLoyaltyUserBenefit'
import AccommodationPackageTileHeaderArea from 'components/OfferPage/Accommodation/AccommodationPackageTileHeaderArea'
import { useAppSelector } from 'hooks/reduxHooks'
import { getIsLuxLoyaltyEnabled } from 'luxLoyalty/selectors/luxLoyaltyFeatureToggles'
import OfferPageLuxLoyaltyRoomUpgradeCallOutBanner from 'components/OfferPage/OfferPageLuxLoyaltyRoomUpgradeCallOutBanner'
import useLuxLoyaltyOfferPageTargetRoomUpgrade from 'components/OfferPage/hooks/useLuxLoyaltyOfferPageTargetRoomUpgrade'

interface Props {
  optionNumber: number;
  offer: App.Offer;
  packageView: OfferPackageOptionView;
  checkIn?: string;
  duration: number;
  flightDestination?: App.Airport;
  previewMode: boolean;
  isLuxPlusEnabled: boolean;
  children?: React.ReactNode;
}

function FlashHotelRateOptionVariant(props: Props) {
  const {
    offer,
    packageView,
    checkIn,
    duration,
    optionNumber,
    flightDestination,
    previewMode,
    isLuxPlusEnabled,
    children,
  } = props

  const { partnership } = useContext(GeoContext)
  const availableForPurchase = useIsAvailableForPurchase()
  const {
    package: pkg,
  } = packageView

  const showPartnership =
    (packageView.hotelAvailable &&
      !!partnership?.hasEarn &&
      offer.canEarnPartnershipPoints)

  const showPrices = (packageView.capacityValid && packageView.hotelAvailable) || previewMode
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const checkOutMoment = useMemo(() => checkInMoment?.clone().add(duration, 'days'), [checkInMoment, duration])

  const { inclusions, bonusInclusions, luxPlusInclusions } = useOfferInclusions(pkg, { checkIn: checkInMoment, checkOut: checkOutMoment })

  const {
    selectedTargetUpgradePackage,
  } = useLuxLoyaltyOfferPageTargetRoomUpgrade(pkg)

  const {
    inclusions: targetUpgradeInclusions,
    bonusInclusions: targetUpgradeBonusInclusions,
    luxPlusInclusions: targetUpgradeLuxPlusInclusions,
  } = useOfferInclusions(selectedTargetUpgradePackage, { checkIn: checkInMoment, checkOut: checkOutMoment })

  const memberPrice = packageView.baseMemberPrice ?? 0
  const showMemberPrice = isLuxPlusEnabled && memberPrice > 0
  const saveAmount = showMemberPrice ? (packageView.basePrice ?? 0) - memberPrice : undefined

  const offerPageDispatch = useOfferPageDispatch()

  const handleOpenCalendar = useCallback<MouseEventHandler<HTMLAnchorElement>>(() => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [offerPageDispatch, pkg.roomType])

  const openCapacityFilter = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [offerPageDispatch, pkg.roomType])

  const pointsEarnCalculationRequests = useMemo<Array<App.LuxLoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLuxLoyaltyPointsCalculatorHotelOptions(offer, { packageView }),
    generateLuxLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      packageView,
      skip: !offer.bundledWithFlightsOnly,
    }),
  ], [offer, packageView])

  const isLuxLoyaltyEnabled = useAppSelector(getIsLuxLoyaltyEnabled)
  const luxLoyaltyRoomUpgradeBenefitDetails = useLuxLoyaltyUserBenefit('hotel_upgrade')
  const hasLuxLoyaltyRoomUpgradesAvailable = !!luxLoyaltyRoomUpgradeBenefitDetails?.remaining

  return (
    <AccommodationPackageTile data-testid={pkg.uniqueKey}>
      {isLuxLoyaltyEnabled && hasLuxLoyaltyRoomUpgradesAvailable && <AccommodationPackageTileHeaderArea>
        <OfferPageLuxLoyaltyRoomUpgradeCallOutBanner package={pkg} />
      </AccommodationPackageTileHeaderArea>}
      <AccommodationPackageTileContentArea>
        <AccommodationPackageTileDetails
          packageNumber={optionNumber}
            // @ts-expect-error not App.HotelPackage
          roomPackage={packageView.package}
          selectedLuxLoyaltyTargetUpgradePackage={selectedTargetUpgradePackage}
        >
          <Group direction="vertical" noDisplayWhenEmpty gap={4}>
            {availableForPurchase && offer.property && <>
              {!selectedTargetUpgradePackage && pkg.roomRate && <FlashCancellationPolicies
                checkInDate={checkIn}
                propertyTimezoneOffset={offer.property.timezoneOffset}
                cancellationPolicy={pkg.roomRate.cancellationPolicy}
              />}
              {selectedTargetUpgradePackage?.roomRate && <FlashCancellationPolicies
                checkInDate={checkIn}
                propertyTimezoneOffset={offer.property.timezoneOffset}
                cancellationPolicy={selectedTargetUpgradePackage.roomRate.cancellationPolicy}
              />}
            </>}
            {!!showPartnership && pkg.partnership && <OfferPackagePartnershipLabel
              offer={offer}
              price={packageView.hotelPrice}
              packagePartnership={pkg.partnership}
            />}
            <LabelGroup>
              {checkIn && <RoomsRemainingLabel roomCount={packageView.availableRooms ?? 0} threshold="rate" />}
              {saveAmount && offer.luxPlus.access !== 'memberOnly' &&
                <CSSBreakpoint min="desktop">
                  <LuxPlusLabelWithModal
                    type="member-price-offer"
                    offer={offer}
                    saveAmount={saveAmount}
                    hideSaveAmountPlus
                    inclusions={luxPlusInclusions}
                  />
                </CSSBreakpoint>}
            </LabelGroup>
            <LuxLoyaltyPoints calculationRequests={pointsEarnCalculationRequests} calculationType={!checkIn ? 'estimate' : 'exact'} />

          </Group>
        </AccommodationPackageTileDetails>
        <div>
          <LargeContentToggle
            maxHeight={208}
            showMoreText="View more inclusions"
            showLessText="View less inclusions"
          >
            <VerticalSpacer gap={8}>
              {!selectedTargetUpgradePackage && <>
                {!!luxPlusInclusions.length && <LuxPlusInclusions
                  luxPlusInclusions={luxPlusInclusions}
                  accommodationOffer={offer}
                  hideUpsellModal
                />}
                <InclusionsSection inclusions={inclusions} bonusInclusions={bonusInclusions} />
              </>}
              {!!selectedTargetUpgradePackage && <>
                {!!targetUpgradeLuxPlusInclusions.length && <LuxPlusInclusions
                  luxPlusInclusions={targetUpgradeLuxPlusInclusions}
                  accommodationOffer={offer}
                  hideUpsellModal
                />}
                <InclusionsSection inclusions={targetUpgradeInclusions} bonusInclusions={targetUpgradeBonusInclusions} />
              </>}
            </VerticalSpacer>
          </LargeContentToggle>
        </div>
      </AccommodationPackageTileContentArea>
      <AccommodationPackageTilePricingArea>
        {children}
        {!children && showPrices && <FlashHotelOptionPriceDetailsVariant
          packageView={packageView}
          flightDestination={flightDestination}
        />}
        {!children && !packageView.capacityValid && (
          <MessageBanner
            kind="informational-primary"
            icon={<LineExclamationCircleIcon />}
            heading="This room cannot accommodate your number of guests"
            description={<>Choose another room or <TextLink size="medium" weight="regular" onClick={openCapacityFilter}>add more rooms</TextLink></>}
          />
        )}
        {!children && !packageView.hotelAvailable && packageView.capacityValid && (
          <MessageBanner
            kind="informational-primary"
            icon={<LineExclamationCircleIcon />}
            heading="This room is sold out on your dates or max number of guests exceeded"
            description={<>Choose another room or <TextLink size="medium" weight="regular" onClick={handleOpenCalendar}>try different dates</TextLink></>}
          />
        )}
      </AccommodationPackageTilePricingArea>
    </AccommodationPackageTile>
  )
}

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State) => ({
  checkIn: state.checkInDate,
  duration: state.duration,
  partnership: appState.geo.partnership,
  previewMode: appState.system.previewMode,
  isLuxPlusEnabled: isLuxPlusEnabled(appState),
}))(FlashHotelRateOptionVariant)
